import React, { useEffect, useState, useContext } from "react";
import { Alert } from "@material-ui/lab";
import { FormikContext } from "formik";
import { useTranslation } from "react-i18next";
import type { IResponse, IResponseWithLoading } from "App/Modules/Models";

type ValidationErrorProps = {
    className?: string;
    genericErrorMessage?: string;
    response: IResponse<any> | IResponseWithLoading<any>;
};

const validationRegex = /^([A-Z][\S]*?(?:\.\S+?))(?:,(.*?))?:.*$/;

export type ValidationResponseErrors = { [key: string]: string[] };

export type ValidationResponse = {
    errors?: ValidationResponseErrors;
};

export const ValidationError = (props: ValidationErrorProps) => {
    const { className, response } = props;
    const genericErrorMessage = props.genericErrorMessage ?? "Validation.GenericErrorSaving";

    const { t } = useTranslation();
    const formik = useContext(FormikContext); // Avoids exception from useFormikContext when formik is not available
    // eslint-disable-next-line @typescript-eslint/unbound-method -- Formik needs funcs to be unbound "function () {}" and not "() => {}" since "this" is injected by Formik
    const { setFieldError } = formik || {};

    const [errorMessage, setErrorMessage] = useState<string>();

    const handleValidationError = (key: string, validationError: string) => {
        try {
            // Below regex will match:
            //  "Validation.Key: Friendly error message"
            //   Example: "Validation.IsRequired: Name is required""
            //  "Validation.Key,ConstraintVariable: Friendly error message"
            //   Example: "Validation.MinLength,6: Password should be at least 6 long"
            // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
            const errorTemplateMatch = validationError.match(validationRegex);

            if (!errorTemplateMatch || errorTemplateMatch.length < 2) {
                // eslint-disable-next-line
                console.warn(
                    'ValidationMessage does not conform to "Prefix.Key: Detailed technical error" ' +
                        "and will therefore not be presented to user",
                    validationError,
                );
                return;
            }

            const errorTemplate = errorTemplateMatch[1];
            const constraint = errorTemplateMatch.length >= 3 ? errorTemplateMatch[2] : undefined;

            if (key === undefined || key === "" || !setFieldError) {
                setErrorMessage(errorTemplate);
            } else {
                setFieldError(key, t(errorTemplate, { constraint: constraint || "" }));
            }
        } catch (e) {
            // Swallow error
            // eslint-disable-next-line
            console.warn("Error translating validation error", e);
        }
    };

    useEffect(() => {
        // Jari, 2021-10-15:
        // response.error.error doesn't exist according to type. Is this an old typo, or will the API return .error as string? Should be .errors according to type.
        if (response?.hasError && (response?.error as any)?.error) {
            const validationErrors = (response.error as ValidationResponse).errors;
            Object.entries(validationErrors).forEach(([key, errors]) => {
                if (errors?.length) {
                    errors.forEach((validationError) => {
                        handleValidationError(key, validationError);
                    });
                }
            });
        } else if (response?.hasError && typeof (response?.error as any)?.error === "string") {
            handleValidationError(undefined, (response.error as any).error as string);
        } else {
            setErrorMessage(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response]);

    return response?.hasError ? (
        <Alert variant="filled" severity="error" classes={{ root: className }}>
            {t(errorMessage || genericErrorMessage)}
        </Alert>
    ) : (
        <></>
    );
};
